import { useState } from 'react'
import { GraduationCap, Plus, Trash2, AlertTriangle, Check, Sparkles, BookOpen, EyeOff } from 'lucide-react'
import AdminNav from '../components/AdminNav'
import { useConfig } from '../context/ConfigContext'
import { useAuth } from '../context/AuthContext'
import { modules as builtInModules } from '../data/modules'
import { slugify, danglingCourses, degreeRequiredModules } from '../data/degrees'
import { courseHasPublishedModules, isCoursePublished, mergeCatalogue, requiredInCourse } from '../data/catalogue'

/**
 * Create degrees and choose the courses each one contains.
 *
 * A degree is what somebody is enrolled in; a course is how it is delivered.
 * Courses are deliberately shareable — the generic Claude course belongs in
 * every degree — and a module completed once counts towards every degree that
 * contains it.
 */
export default function AdminDegreesPage() {
  const { degrees, departments, seeded, saveDegrees, customModules, allCourses } = useConfig()
  const { user } = useAuth()
  // Matches firestore.rules: admins and managers write config/*. The pages
  // and the rules have to agree — offering a button the server refuses is how
  // the manager tier went wrong the first time. See SECURITY-MODEL.md.
  const canEdit = user?.isAdmin === true || user?.isManager === true
  const [draftName, setDraftName] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  /**
   * Deliberately NOT filtered by whether trainees can see the course.
   *
   * A hidden course is still IN the degrees that contain it — hiding sets a
   * flag on the course and never touches `degree.tracks`. Filtering it out
   * here would show an admin a degree that appears to have lost a course it
   * never lost, with no way to see or change the membership, and
   * `danglingCourses` would not warn either: it only catches ids missing from
   * the course list entirely, and a hidden course is still in it.
   */
  const publishedCourses = (allCourses || []).filter((t) =>
    courseHasPublishedModules(t.id, customModules || []))
  const dangling = danglingCourses(degrees, allCourses, customModules || [])

  const getCourseCount = (courseId) => {
    const course = (allCourses || []).find((t) => String(t.id) === String(courseId)) || { id: courseId }
    return requiredInCourse(course, mergeCatalogue(builtInModules, customModules || [])).length
  }

  async function commit(next) {
    setSaving(true)
    setError(null)
    try {
      await saveDegrees(next)
    } catch (err) {
      console.error('Failed to save degrees:', err)
      setError('That did not save — check you still have admin or manager rights.')
    } finally {
      setSaving(false)
    }
  }

  function toggleCourse(degreeId, courseId) {
    commit(degrees.map((d) => {
      if (d.id !== degreeId) return d
      const has = (d.tracks || []).includes(courseId)
      return { ...d, tracks: has ? d.tracks.filter((t) => t !== courseId) : [...(d.tracks || []), courseId] }
    }))
  }

  function addDegree() {
    const trimmed = draftName.trim()
    if (!trimmed) return
    const id = slugify(trimmed)
    if (degrees.some((d) => d.id === id)) {
      setError(`A degree with the id "${id}" already exists.`)
      return
    }
    commit([...degrees, { id, name: trimmed, description: '', tracks: [] }])
    setDraftName('')
  }

  function setDescription(degreeId, description) {
    commit(degrees.map((d) => (d.id === degreeId ? { ...d, description } : d)))
  }

  function removeDegree(degree) {
    const conferring = departments.filter((dept) => (dept.degrees || []).includes(degree.id))
    const warning = conferring.length
      ? `\n\n${conferring.map((d) => d.name).join(' and ')} confer${conferring.length === 1 ? 's' : ''} this degree — that mapping will be left pointing at nothing.`
      : ''
    if (!window.confirm(`Delete the ${degree.name} degree?${warning}\n\nNobody loses any completed module: progress is stored per module, not per degree.`)) return
    commit(degrees.filter((d) => d.id !== degree.id))
  }

  return (
    <div className="mx-auto max-w-4xl px-4 py-8">
      <AdminNav />

      <div className="mb-6 flex items-start gap-3">
        <GraduationCap className="mt-1 h-6 w-6 text-purple-600" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Degrees</h1>
          <p className="mt-1 text-sm text-gray-500">
            A degree is a curriculum pathway combining one or more courses for a role or department.
            Trainee completion is calculated across all courses in their degrees.
          </p>
        </div>
      </div>

      <div className="mb-6 rounded-xl border border-indigo-100 bg-indigo-50/60 p-4 text-xs text-indigo-900 flex items-start gap-3">
        <Sparkles className="h-5 w-5 text-indigo-600 shrink-0 mt-0.5" />
        <div className="space-y-1">
          <p className="font-semibold text-indigo-950">How creating a custom degree works:</p>
          <p className="text-indigo-800 leading-relaxed">
            1. Create a degree with a descriptive name (e.g. <em>Ad Operations</em> or <em>Senior Analyst</em>).<br/>
            2. Toggle on the required courses below.<br/>
            3. Head to <strong>Departments</strong> to confer it automatically by team, or <strong>People</strong> to award it directly to individual crew members.
          </p>
        </div>
      </div>

      {!seeded.degrees && (
        <p className="mb-4 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-800">
          These are the starting degrees that ship with the app. The first change you make saves
          them properly.
        </p>
      )}

      {error && (
        <p className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>
      )}

      {dangling.length > 0 && (
        <p className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          <AlertTriangle className="mr-1 inline h-4 w-4" />
          {dangling.map((d) => `${d.degree} includes a course called "${d.track}" that no longer exists`).join('; ')}.
        </p>
      )}

      <div className="space-y-4">
        {degrees.map((degree) => {
          const moduleCount = degreeRequiredModules(degree.id, degrees, allCourses, customModules || []).length
          return (
            <div key={degree.id} className="rounded-xl border border-gray-200 bg-white p-5">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <h2 className="font-semibold text-gray-900">{degree.name}</h2>
                  <input
                    type="text"
                    defaultValue={degree.description}
                    readOnly={!canEdit}
                    onBlur={(e) => canEdit && e.target.value !== degree.description && setDescription(degree.id, e.target.value)}
                    placeholder="One line describing who this is for…"
                    className="mt-1 w-full rounded border border-transparent px-1 py-0.5 text-sm text-gray-600 hover:border-gray-200 focus:border-blue-300 focus:outline-none"
                  />
                </div>
                {canEdit && <button
                  onClick={() => removeDegree(degree)}
                  className="inline-flex shrink-0 items-center gap-1 rounded px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50"
                >
                  <Trash2 className="h-3 w-3" /> Delete
                </button>}
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                {publishedCourses.map((track) => {
                  const on = (degree.tracks || []).includes(track.id)
                  const count = getCourseCount(track.id)
                  const hidden = !isCoursePublished(track)
                  return (
                    <button
                      key={track.id}
                      disabled={saving || !canEdit}
                      onClick={() => toggleCourse(degree.id, track.id)}
                      className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors disabled:opacity-50 ${
                        on
                          ? 'border-purple-300 bg-purple-50 text-purple-800 hover:bg-purple-100'
                          : 'border-gray-200 bg-white text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      {on ? <Check className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
                      {track.name}
                      <span className="text-xs opacity-60">({count})</span>
                      {/* Still in the degree, just not shown to trainees. Said
                          here because the alternative — dropping it from this
                          list — reads as a course the degree has lost. */}
                      {hidden && (
                        <span className="inline-flex items-center gap-1 rounded border border-gray-300 bg-gray-100 px-1 py-0.5 text-[10px] font-semibold text-gray-600">
                          <EyeOff className="h-2.5 w-2.5" /> hidden
                        </span>
                      )}
                    </button>
                  )
                })}
              </div>

              <p className="mt-3 text-xs text-gray-400">
                {moduleCount} module{moduleCount === 1 ? '' : 's'} counted towards this degree
                {moduleCount === 0 ? ' — nobody can make progress on it yet' : ''}
              </p>
            </div>
          )
        })}
      </div>

      {canEdit && <div className="mt-6 rounded-xl border border-dashed border-gray-300 bg-white p-5">
        <h2 className="text-sm font-semibold text-gray-700">New degree</h2>
        <div className="mt-2 flex gap-2">
          <input
            type="text"
            value={draftName}
            onChange={(e) => setDraftName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && addDegree()}
            placeholder="Ad Operations"
            className="flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-blue-300 focus:outline-none"
          />
          <button
            onClick={addDegree}
            disabled={saving || !draftName.trim()}
            className="inline-flex items-center gap-1.5 rounded-lg bg-purple-600 px-4 py-2 text-sm font-semibold text-white hover:bg-purple-700 disabled:bg-gray-300"
          >
            <Plus className="h-4 w-4" /> Create
          </button>
        </div>
        <p className="mt-2 text-xs text-gray-400">
          Then choose its courses. A degree with no courses is harmless — it simply counts for
          nothing until you give it some.
        </p>
      </div>}
    </div>
  )
}
